/**
 * Reading and writing one person's alert opt-ins for one workspace.
 *
 * PER USER, PER WORKSPACE, and the primary key says so. An agency runs several
 * clients from one login; the person who owns client A must be able to hear
 * about A without hearing about B, which a single per-user switch could not
 * express.
 *
 * THE WRITE GOES THROUGH AN RPC, NOT AN UPSERT, and that was learned the hard
 * way. `authenticated` has no INSERT or UPDATE on this table at all (migration
 * 074): `last_sent_on` and `last_item_keys` are the worker's bookkeeping, and
 * the suppression list decides what never gets mailed again, so a settings
 * screen must not be able to name them. A PostgREST upsert cannot work under
 * that rule — it compiles to ON CONFLICT DO UPDATE over every column in the
 * payload, including the `user_id`/`workspace_id` conflict target, and the
 * first tick of a checkbox came back a flat 403. set_alert_preferences takes
 * the four booleans, fills the user from the token, and cannot express
 * anything else.
 *
 * No row means "everything off": every column defaults false, so an absent row
 * and a row of falses are the same state, and the read below does not need to
 * create one just to answer.
 *
 * @module services/notifications/notificationPreferencesService
 */

import { getSupabaseClient, isSupabaseConfigured } from "@/config/supabase";
import { logError } from "@/services/logger";
import type { AlertPreferences } from "./digestBuilder";
import type { ServiceResult } from "@/types";
import type { TFn } from "@/utils/channelCorrelation";

/** Nobody is enrolled into mail they did not ask for. */
export const ALERTS_OFF: AlertPreferences = {
  alertCpaWorse: false,
  alertConnection: false,
  alertNoConversions: false,
  alertOverPlan: false,
};

interface Row {
  alert_cpa_worse: boolean;
  alert_connection: boolean;
  alert_no_conversions: boolean;
  alert_over_plan: boolean;
}

/**
 * Read this user's opt-ins for a workspace.
 *
 * @param workspaceId - the workspace the toggles apply to
 * @returns the preferences; all-off when no row exists yet
 */
export const fetchAlertPreferences = async (
  workspaceId: string,
  t: TFn,
): Promise<ServiceResult<AlertPreferences>> => {
  if (!isSupabaseConfigured()) return { success: true, data: ALERTS_OFF };

  try {
    const supabase = getSupabaseClient();
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) return { success: false, error: t("settings.alertsNotSignedIn") };

    const { data, error } = await supabase
      .from("notification_preferences")
      .select("alert_cpa_worse, alert_connection, alert_no_conversions, alert_over_plan")
      .eq("user_id", userId)
      .eq("workspace_id", workspaceId)
      .maybeSingle();

    if (error) {
      logError("Notifications", "Failed to read alert preferences", error);
      return { success: false, error: error.message };
    }

    const row = data as Row | null;
    // A missing row is not a failure and not an unknown — it is the default
    // state, which is off.
    if (!row) return { success: true, data: ALERTS_OFF };

    return {
      success: true,
      data: {
        alertCpaWorse: row.alert_cpa_worse,
        alertConnection: row.alert_connection,
        alertNoConversions: row.alert_no_conversions,
        alertOverPlan: row.alert_over_plan,
      },
    };
  } catch (err) {
    logError("Notifications", "Alert preferences read error", err);
    return { success: false, error: t("settings.alertsUnexpected") };
  }
};

/**
 * Save this user's opt-ins for a workspace.
 *
 * @param workspaceId - the workspace the toggles apply to
 * @param prefs - the full set; every toggle is written, so a cleared one clears
 * @returns success, or the reason it failed — the caller must not assume saved
 */
export const saveAlertPreferences = async (
  workspaceId: string,
  prefs: AlertPreferences,
  t: TFn,
): Promise<ServiceResult<AlertPreferences>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("settings.alertsDemoMode") };
  }

  try {
    const supabase = getSupabaseClient();
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) return { success: false, error: t("settings.alertsNotSignedIn") };

    // Literal function name and inline argument object — the rpcArgumentNames
    // guard only sees literal call sites, and a helper wrapping rpc() would
    // hide this one from it.
    const { error } = await supabase.rpc("set_alert_preferences", {
      p_workspace: workspaceId,
      p_cpa_worse: prefs.alertCpaWorse,
      p_connection: prefs.alertConnection,
      p_no_conversions: prefs.alertNoConversions,
      p_over_plan: prefs.alertOverPlan,
    });

    if (error) {
      logError("Notifications", "Failed to save alert preferences", error);
      return { success: false, error: error.message };
    }
    return { success: true, data: prefs };
  } catch (err) {
    logError("Notifications", "Alert preferences save error", err);
    return { success: false, error: t("settings.alertsUnexpected") };
  }
};
